<template>
    <span :class="{ 'truncate': truncate }">
        <template v-if="column === 'name'">
            <button
                class="group-hover:text-blue-500 | duration-200"
                @click="$emit('update-contact')"
            >
                <nio-optional :value="fullName" />
                <nio-icon
                    class="ml-2"
                    name="regular/notes-paper-text"
                    v-tooltip="contact.notes"
                    v-if="contact.notes"
                />
                <nio-icon
                    class="opacity-0 text-blue-500 ml-2 | group-hover:opacity-100 | transition-opacity duration-200"
                    name="bold/arrow-button-circle-right-1"
                />
            </button>
        </template>
        <template v-if="column === 'phone'">
            <contact-phones :contact="contact" />
        </template>
        <template v-if="column === 'email'">
            <nio-optional :value="contact.email" />
        </template>
        <template v-if="column === 'locale'">
            {{ locale }}
        </template>
        <template v-if="column === 'subscribed'">
            <nio-icon
                class="text-lg text-green-500"
                name="bold/check-circle-1"
                v-if="contact.subscribed"
            />
            <nio-icon
                class="text-lg text-gray-500"
                name="bold/remove-circle"
                v-else
            />
        </template>
        <template v-if="column === 'consent'">
            <nio-icon
                class="text-lg text-gray-500"
                name="bold/remove-circle"
                v-tooltip="$t('chat.messageList.consentExpiration')"
                v-if="!contact.consent"
            />
            <nio-icon
                class="text-lg text-green-500"
                name="bold/check-circle-1"
                v-tooltip="$t('chat.messageList.consentConfirmed')"
                v-if="contact.consent && contact.consent_confirmed_date"
            />
            <nio-icon
                class="text-lg text-blue-500"
                name="bold/check-circle-1"
                v-tooltip="$t('chat.messageList.consentLimitDate', {'date': formatDate(contact.consent_limit_date)})"
                v-if="contact.consent && !contact.consent_confirmed_date && contact.consent_limit_date"
            />
            <nio-icon
                class="text-lg text-green-500"
                name="bold/check-circle-1"
                v-if="contact.consent && !contact.consent_confirmed_date && !contact.consent_limit_date"
            />
        </template>
        <template v-if="column === 'actions'">
            <nio-dropdown>
                <template #trigger>
                    <button class="link-gray text-base">
                        <nio-icon name="bold/navigation-menu-horizontal" />
                    </button>
                </template>

                <nio-dropdown-item
                    icon="regular/messages-bubble-forward"
                    @click="openConversation(conversationId)"
                    v-if="conversationId"
                >
                    {{ $t('contacts.contactsList.goToConversation') }}
                </nio-dropdown-item>
                <div v-tooltip="missingValidProviderPhoneTooltip()">
                    <nio-dropdown-item
                        :disabled="!contact.phones.length || !userValidProviderPhones"
                        icon="regular/messages-bubble-add"
                        @click="triggerCreateConversation"
                        v-if="!conversationId"
                    >
                        {{ $t('contacts.contactsList.createConversation') }}
                    </nio-dropdown-item>
                </div>
                <nio-dropdown-item
                    icon="regular/calendar-add"
                    @click="$emit('create-appointment')"
                    v-if="hasAccessToAppointment"
                >
                    {{ $t('contacts.contactsList.createAppointment') }}
                </nio-dropdown-item>
                <nio-dropdown-item
                    icon="regular/accounting-invoice-mail"
                    @click="$emit('create-payment')"
                    v-if="hasAccessToPayment"
                >
                    {{ $t('contacts.contactsList.createPayment') }}
                </nio-dropdown-item>
                <nio-dropdown-item icon="regular/pencil-write" @click="$emit('update-contact')">
                    {{ $t('contacts.contactsList.edit') }}
                </nio-dropdown-item>
                <hr>
                <nio-dropdown-item
                    class="transition duration-300 hover:text-red-500"
                    :disabled="!!contact.related_id"
                    icon="regular/bin-2-alternate"
                    @click="$emit('delete-contact')"
                >
                    {{ $t('contacts.contactsList.delete') }}
                </nio-dropdown-item>
            </nio-dropdown>
        </template>
    </span>
</template>

<script>
    import {
        mapGetters,
        mapMutations,
        mapState,
    } from 'vuex';

    import PhoneValidation from '@/mixins/PhoneValidation.js';
    import ContactPhones from '@/components/ContactPhones.vue';

    export default {
        components: {
            ContactPhones,
        },

        mixins: [PhoneValidation],

        props: {
            column: {
                type: String,
                required: true,
            },
            contact: {
                type: Object,
                required: true,
            },
            truncate: {
                type: Boolean,
                default: true,
            },
        },

        computed: {
            ...mapState([
                'authUser',
            ]),

            ...mapGetters([
                'hasAccessToAppointment',
                'hasAccessToPayment',
            ]),

            fullName() {
                return this.formatFullName(this.contact);
            },

            locale() {
                return this.formatLocale(this.contact.locale);
            },

            conversationId() {
                let conversation;

                if (!empty(this.contact.conversations)) {
                    conversation = this.contact.conversations.find(c => {
                        if (!empty(c.users)) {
                            return c.users.find(u => {
                                return u.id == this.authUser.id;
                            });
                        }

                        return false;
                    });
                }

                return !empty(conversation) ? conversation.id : null;
            },

            userValidProviderPhones() {
                return this.authUser.account.userAvailableProviderPhones(this.authUser.id).length > 0;
            },
        },

        methods: {
            ...mapMutations('contact', [
                'setEditingContact',
            ]),

            openConversation(conversationId) {
                this.$router.push({
                    name: 'chat',
                    params: {
                        id: conversationId,
                    },
                });
            },

            async triggerCreateConversation() {
                try {
                    await this.triggerValidatePhones(this.contact);

                    this.createConversation();
                } catch (error) {
                    this.$notify.show({
                        type: 'error',
                        message: this.$t('chat.newConversation.errors.invalidNumberMessage'),
                    });
                }
            },

            async createConversation() {
                try {
                    const response = await this.$axios.post('conversations', {
                        account_id: this.contact.account_id,
                        contact_id: this.contact.id,
                    });

                    this.openConversation(response.data.data.id);
                } catch (error) {
                    if (!empty(error.response)) {
                        if (error.response.status == 400) {
                            this.$notify.show({
                                type: 'warning',
                                message: this.$t('chat.newConversation.errors.conversationAlreadyExist'),
                            });

                            return;
                        }

                        if (error.response.status == 422) {
                            this.$notify.show({
                                type: 'warning',
                                message: this.$t('chat.newConversation.errors.limitExceeded'),
                            });

                            return;
                        }
                    }

                    this.$notify.show({
                        type: 'error',
                        message: this.$t('global.errors.unknown'),
                    });
                }
            },

            missingValidProviderPhoneTooltip() {
                return this.userValidProviderPhones ? '' : this.$t('global.disabled.missingValidProviderPhoneTooltip');
            },
        },
    };
</script>
